/**
 * collapseProgressOutput — make terminal control output readable on a
 * non-terminal surface (chat tool block, script-runner pane, and the
 * LLM context fed back to the model).
 *
 * Two transforms, in order:
 *  1. Strip ANSI / VT escape sequences — SGR colour, cursor moves,
 *     erase-line (`\x1B[2K`, `\x1B[K`), OSC titles, charset selects, and
 *     other Fe escapes. This is a SUPERSET of the ANSI-only strip the
 *     @sync-OpenCode BashOutput previously did inline.
 *  2. Collapse carriage-return line rewrites — `git pull`/`curl`/`pip`
 *     emit `…10%\r…20%\r…100%` on ONE line; a real terminal shows only
 *     the final state. Keep the last segment per line instead of the
 *     ugly concatenated run.
 *
 * Pure, dependency-free, idempotent. Preserves real newlines and overall
 * structure; never trims or truncates (callers do their own capping —
 * run this BEFORE truncation so a multi-MB progress blob collapses
 * before the size cap, not after).
 *
 * Pragmatic, not a full VT parser: "last write per line" covers
 * git/curl/pip/npm/wget/docker progress; it does not column-merge a
 * shorter overwrite onto a longer previous line (rare, and not worth a
 * terminal emulator here).
 */

// CSI (params 0x30–0x3F, intermediates 0x20–0x2F, final 0x40–0x7E —
// covers SGR/cursor/erase), OSC (BEL- or ST-terminated), charset
// designators (`\x1B(B` …), then other two-char Fe escapes. The `\[`
// and `\]` alternatives precede the general class so openers aren't
// mis-eaten.
const ANSI_RE =
  // eslint-disable-next-line no-control-regex
  /\x1B(?:\[[0-?]*[ -/]*[@-~]|\][^\x07\x1B]*(?:\x07|\x1B\\)|[()][AB0-2]|[@-Z\\-_])/g

export function collapseProgressOutput(text: string): string {
  if (!text) return text
  // Fast path: nothing terminal-ish — the overwhelming common case.
  if (text.indexOf('\x1B') === -1 && text.indexOf('\r') === -1) return text

  const noAnsi = text.replace(ANSI_RE, '')
  // CRLF → LF FIRST, so genuine line breaks aren't eaten by the
  // per-line carriage-return collapse below.
  const lf = noAnsi.replace(/\r\n/g, '\n')
  if (lf.indexOf('\r') === -1) return lf

  return lf
    .split('\n')
    .map((line) => {
      const i = line.lastIndexOf('\r')
      return i === -1 ? line : line.slice(i + 1)
    })
    .join('\n')
}

/**
 * Escape a string for safe embedding in HTML **text or attribute** content —
 * so a value with `&`, `<`, `>`, `"` or `'` cannot malform the surrounding
 * markup or inject (e.g. a user-chosen app name or an operator brand rendered
 * into a server-scaffolded page). Both quote characters are escaped, so it is
 * safe inside single- OR double-quoted attributes. NOT sufficient for a
 * `<script>` body (use JSON + `<`/`/` escaping there) or a URL context. Pure,
 * dependency-free.
 */
export function htmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
